import { TEAM_LABEL } from '../../shared/teams';
import type { FirstHiderChoice } from '../state/setup';

/**
 * v28 BOLT-138 — "who hides first" is a slider, not a row of buttons.
 *
 * Jake, twice across two versions: "left is orange, middle is random, right is
 * white… Aim to make this a more intuitive experience with fewer words."
 *
 * The geometry says what three labelled buttons had to spell out: the two ENDS
 * are the two teams, the MIDDLE is the coin flip, and random reads as the
 * neutral default rather than a third thing to pick.
 *
 * A real `<input type="range">`, not a div with a drag handler, because
 * acceptance 6 requires it be operable WITHOUT a drag: arrow keys work, VoiceOver
 * and TalkBack treat it as adjustable, and `aria-valuetext` announces the TEAM
 * BY NAME at every detent so colour is never the only carrier of meaning. The
 * visible value line carries the same name for sighted users.
 */
export const FIRST_HIDER_ORDER: readonly FirstHiderChoice[] = ['A', 'random', 'B'];

export function firstHiderLabel(choice: FirstHiderChoice): string {
  return choice === 'random' ? 'Random' : TEAM_LABEL[choice];
}

export function FirstHiderSlider({
  value,
  onChange,
  youAre,
}: {
  value: FirstHiderChoice;
  onChange: (choice: FirstHiderChoice) => void;
  youAre?: 'A' | 'B' | null;
}) {
  const index = Math.max(0, FIRST_HIDER_ORDER.indexOf(value));
  const label = firstHiderLabel(value);
  return (
    <div className="fhs" data-testid="first-hider-slider" data-choice={value}>
      <div className="fhs__track-row">
        {/* Colour end caps. They are the geometry, not copy: the NAME is
            always in the value line below and in aria-valuetext. */}
        <span className="fhs__cap fhs__cap--a" aria-hidden="true" />
        <input
          className="fhs__input"
          type="range"
          min={0}
          max={FIRST_HIDER_ORDER.length - 1}
          step={1}
          value={index}
          aria-label="Who hides first"
          aria-valuetext={label}
          data-testid="first-hider-range"
          onChange={(e) => onChange(FIRST_HIDER_ORDER[Number(e.target.value)])}
        />
        <span className="fhs__cap fhs__cap--b" aria-hidden="true" />
      </div>
      <div className="fhs__value" data-testid="first-hider-value">
        {label}
        {youAre && value === youAre ? ' (you)' : ''}
      </div>
    </div>
  );
}
